import moment from 'moment'

import { LocationMetricRow, Order, OrderBy } from './types'

/**
 * Rounds a value down to the nearest tenth (0.1)
 * Used for Prime Cost metrics where lower values are better
 * Example: 2.36 -> 2.3, 2.34 -> 2.3
 * @param value - The value to round down
 * @returns The rounded down value
 */
export const roundDownToTenth = (value: number): number => {
  return Math.floor(value * 10) / 10
}

/**
 * Rounds a value up to the nearest tenth (0.1)
 * Used for Guest metrics where higher values are better
 * Example: 77.08 -> 77.1, 2.81 -> 2.9
 * @param value - The value to round up
 * @returns The rounded up value
 */
export const roundUpToTenth = (value: number): number => {
  return Math.ceil(value * 10) / 10
}

/**
 * Applies rounding to a percentage value stored as a decimal
 * For Prime Cost metrics (lower is better), rounds down
 * Example: 0.0236 (2.36%) -> 0.023 (2.3%)
 * @param value - The decimal percentage value (e.g., 0.023 for 2.3%)
 * @param roundingFn - The rounding function to apply
 * @returns The rounded decimal percentage value
 */
export const applyRoundingToPercentage = (
  value: number,
  roundingFn: (n: number) => number,
): number => {
  // Convert decimal to percentage (0.023 -> 2.3)
  const percentageValue = value * 100
  // Apply rounding (2.36 -> 2.3)
  const roundedPercentage = roundingFn(percentageValue)
  // Convert back to decimal (2.3 -> 0.023)
  return roundedPercentage / 100
}

/**
 * Gets the rounded value for Prime Cost metrics (rounds down, lower is better)
 * Handles both percentage and non-percentage values
 * @param value - The metric value
 * @param isPercentage - Whether the value is stored as a decimal percentage
 * @returns The rounded value for comparison
 */
export const getRoundedPrimeCostValue = (
  value: number,
  isPercentage: boolean = true,
): number => {
  if (isPercentage) {
    return applyRoundingToPercentage(value, roundDownToTenth)
  }
  return roundDownToTenth(value)
}

/**
 * Gets the rounded value for Guest metrics (rounds up, higher is better)
 * Handles both percentage and non-percentage values
 * @param value - The metric value
 * @param isPercentage - Whether the value is stored as a decimal percentage
 * @returns The rounded value for comparison
 */
export const getRoundedGuestValue = (
  value: number,
  isPercentage: boolean = true,
): number => {
  if (isPercentage) {
    return applyRoundingToPercentage(value, roundUpToTenth)
  }
  return roundUpToTenth(value)
}

// Format number with commas for thousands
export const formatNumber = (
  value: number | undefined,
  isCurrency: boolean = false,
  isPercentage: boolean = false,
  decimals?: number,
  metricType?: 'primeCost' | 'guest' | null,
): string => {
  if (value === undefined) return '-'

  // Convert cents to dollars for currency values
  if (isCurrency) {
    value = value / 100
  }

  // Multiply percentage values by 100
  if (isPercentage) {
    value = value * 100
  }

  // Apply rounding based on metric type
  if (metricType === 'primeCost') {
    // Round down for Prime Cost metrics (lower is better)
    value = roundDownToTenth(value)
  } else if (metricType === 'guest') {
    // Round up for Guest metrics (higher is better)
    value = roundUpToTenth(value)
  }

  let formatOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }

  if (isCurrency) {
    formatOptions = {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: Math.abs(value) < 1000 ? 2 : 0,
      maximumFractionDigits: Math.abs(value) < 1000 ? 2 : 0,
    }
  } else if (decimals !== undefined) {
    formatOptions.minimumFractionDigits = decimals
    formatOptions.maximumFractionDigits = decimals
  } else if (metricType) {
    // For Prime Cost and Guest metrics, display 1 decimal place
    formatOptions.minimumFractionDigits = 1
    formatOptions.maximumFractionDigits = 1
  }

  const formattedValue = new Intl.NumberFormat('en-US', formatOptions).format(
    value,
  )
  return isPercentage ? `${formattedValue}%` : formattedValue
}

// Get comparison function for sorting
export const getCompareFunction = (orderBy: OrderBy, order: Order) => {
  return (a: LocationMetricRow, b: LocationMetricRow) => {
    let valueA: number | string = 0
    let valueB: number | string = 0

    switch (orderBy) {
      case 'location':
        valueA = `${a.locationCode} - ${a.locationName}`
        valueB = `${b.locationCode} - ${b.locationName}`
        break
      case 'bonus_attainment_percent_quarter':
        valueA = a.bonusAttainment?.value ?? 0
        valueB = b.bonusAttainment?.value ?? 0
        break
      case 'net_sales':
        valueA = a.netSales?.value ?? 0
        valueB = b.netSales?.value ?? 0
        break
      case 'average_weekly_sales_quarter':
        valueA = a.avgWeeklySales?.value ?? 0
        valueB = b.avgWeeklySales?.value ?? 0
        break
      case 'bonus_sales_to_next_band':
        valueA = a.bonusSalesToNextBand?.value ?? 0
        valueB = b.bonusSalesToNextBand?.value ?? 0
        break
      case 'food_and_beverage_variance':
      case 'large_meat_variance':
        valueA = a.foodVariance?.value ?? 0
        valueB = b.foodVariance?.value ?? 0
        break
      case 'paper_variance':
        valueA = a.paperVariance?.value ?? 0
        valueB = b.paperVariance?.value ?? 0
        break
      case 'labor_variance':
        valueA = a.laborVariance?.value ?? 0
        valueB = b.laborVariance?.value ?? 0
        break
      case 'total_prime_cost_variance':
        valueA = a.totalVariance?.value ?? 0
        valueB = b.totalVariance?.value ?? 0
        break
      case 'qualtrics_osat_percent':
        valueA = a.osat?.value ?? 0
        valueB = b.osat?.value ?? 0
        break
      case 'google_rating':
        valueA = a.google?.value ?? 0
        valueB = b.google?.value ?? 0
        break
      case 'delivery_avg_customer_review_score_doordash':
        valueA = a.delivery?.value ?? 0
        valueB = b.delivery?.value ?? 0
        break
      case 'hme_goal_percent':
        valueA = a.sosAttainment?.value ?? 0
        valueB = b.sosAttainment?.value ?? 0
        break
      case 'qualtrics_drive_thru_wait_time_percent':
        valueA = a.sosQualtricsAttainment?.value ?? 0
        valueB = b.sosQualtricsAttainment?.value ?? 0
        break
      case 'bonus_op_prime_cost_quarter':
        valueA = a.opPrimeCostBonus?.value ?? 0
        valueB = b.opPrimeCostBonus?.value ?? 0
        break
      case 'bonus_op_guest_metric_quarter':
        valueA = a.opGuestBonus?.value ?? 0
        valueB = b.opGuestBonus?.value ?? 0
        break
      case 'bonus_op_total_quarter':
        valueA = a.opTotalBonus?.value ?? 0
        valueB = b.opTotalBonus?.value ?? 0
        break
      case 'bonus_op_missed_opportunity_quarter':
        valueA = a.opMissedOpportunity?.value ?? 0
        valueB = b.opMissedOpportunity?.value ?? 0
        break
      case 'bonus_manager_prime_cost_quarter':
        valueA = a.managerPrimeCostBonus?.value ?? 0
        valueB = b.managerPrimeCostBonus?.value ?? 0
        break
      case 'bonus_manager_guest_metric_quarter':
        valueA = a.managerGuestBonus?.value ?? 0
        valueB = b.managerGuestBonus?.value ?? 0
        break
      case 'bonus_manager_total_quarter':
        valueA = a.managerTotalBonus?.value ?? 0
        valueB = b.managerTotalBonus?.value ?? 0
        break
      case 'bonus_manager_missed_opportunity_quarter':
        valueA = a.managerMissedOpportunity?.value ?? 0
        valueB = b.managerMissedOpportunity?.value ?? 0
        break
    }

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return order === 'asc'
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA)
    }

    return order === 'asc'
      ? (valueA as number) - (valueB as number)
      : (valueB as number) - (valueA as number)
  }
}

// Sort table data
export const sortTableData = (
  tableData: LocationMetricRow[],
  orderBy: OrderBy,
  order: Order,
): LocationMetricRow[] => {
  if (tableData.length === 0) {
    return []
  }

  const compare = getCompareFunction(orderBy, order)

  // Group rows by dataset
  const datasetGroups = tableData.reduce((groups, row) => {
    const datasetId = row.datasetId || 1
    if (!groups[datasetId]) {
      groups[datasetId] = {
        rows: [],
        summaryRow: null,
      }
    }

    if (row.locationId < 0) {
      // Summary rows (negative IDs: -1, -3, -5, -7, etc.)
      groups[datasetId].summaryRow = row
    } else {
      // Regular rows (positive IDs)
      groups[datasetId].rows.push(row)
    }

    return groups
  }, {} as { [datasetId: number]: { rows: LocationMetricRow[]; summaryRow: LocationMetricRow | null } })

  // Sort each dataset's rows independently
  Object.values(datasetGroups).forEach((group) => {
    group.rows.sort(compare)
  })

  // Combine sorted rows from all datasets, preserving order and adding summary rows
  const sortedResult: LocationMetricRow[] = []

  // Add dataset 1 (main dataset)
  if (datasetGroups[1]) {
    sortedResult.push(...datasetGroups[1].rows)
    if (datasetGroups[1].summaryRow) {
      sortedResult.push(datasetGroups[1].summaryRow)
    }
  }

  // Add all other datasets (other market leaders) in order
  const datasetIds = Object.keys(datasetGroups)
    .map(Number)
    .sort((a, b) => a - b)
    .filter((id) => id > 1) // Exclude dataset 1 as it's already added

  for (const datasetId of datasetIds) {
    if (datasetGroups[datasetId]) {
      sortedResult.push(...datasetGroups[datasetId].rows)
      if (datasetGroups[datasetId].summaryRow) {
        sortedResult.push(datasetGroups[datasetId].summaryRow)
      }
    }
  }

  return sortedResult
}

// Get target version based on date
export const getTargetVersion = (startDate: string): number => {
  if (moment.utc(startDate).isSameOrAfter(moment.utc('2026-01-03'))) {
    // Version 3: Q2 FQ2 2026 (starting 2026-01-03): 5-tier threshold systems for Google Review and DoorDash
    return 3
  }
  if (moment.utc(startDate).isSameOrAfter(moment.utc('2025-10-04'))) {
    // Version 2: DoorDash target changed back to 4.0 (starting 2025-10-04)
    return 2
  }
  if (moment.utc(startDate).isSameOrAfter(moment.utc('2025-06-28'))) {
    return 1
  }
  return 0
}

// Adjust end date if it's after today
export const adjustEndDate = (endDate: string): string => {
  if (endDate >= moment().format('YYYY-MM-DD')) {
    const today = moment()
    return moment()
      .day(today.day() > 5 ? 5 : -2)
      .format('YYYY-MM-DD')
  }
  return endDate
}

// Adjust the data update date if it's after today
export const getUpdateDate = (
  endDate: string,
  laborCoveredEndDate?: string | null,
): string => {
  if (endDate >= moment().format('YYYY-MM-DD')) {
    // why: there is no stored "last updated" timestamp - the header derives
    // it. The R365 lookback posts a week's labor on the Wednesday after that
    // week closes, so the last posted labor Friday + 5 days IS the Wednesday
    // the visible data actually landed (ENG-5456, anchor per Gerald
    // 2026-07-21: the lookback Wednesday, e.g. 7/15 for week ending 7/10).
    // Deriving from posted-labor coverage instead of the clock keeps the date
    // from advancing when a lookback is late or fails, so the header always
    // agrees with the Labor Variance clamp (ENG-5468).
    if (laborCoveredEndDate) {
      return moment(laborCoveredEndDate, 'YYYY-MM-DD')
        .add(5, 'days')
        .format('YYYY-MM-DD')
    }

    // why: zero-coverage fallback (no posted labor week inside the range) -
    // derive the most recent Wednesday from the clock instead.
    const today = moment()
    return moment()
      .day(today.day() >= 3 ? 3 : -4) // This week's Wednesday if it has occurred, else last week's
      .format('YYYY-MM-DD')
  }
  return endDate
}
